import React, { useCallback, useEffect } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import EmptyHeaderLayout from "../../Components/Molecules/EmptyHeaderLayout";
import LoadingPage from "../LoadingPage";
import { useOnboardingStore } from "../../Store/Onboarding";
import { ReactComponent as ArrowRightIcon } from "../../Assets/ActionIcons/ArrowRightIcon.svg";
import { useNavigate } from "react-router-dom";
import { PaymentsPartnerCountry } from "../../Contracts/Common";
import { AdyenLegalEntityType } from "../../Contracts/Onboarding";
import { isValidPhoneNumber, AsYouType, parsePhoneNumberFromString } from "libphonenumber-js/min";

interface OnboardingForm {
  country: string;
  organizationType: string;
  legalEntityName: string;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
  termsChecked: boolean;
}

const OnboardingStartForm: React.FC = () => {
  const navigate = useNavigate();
  const { isLoading, onboardingStartView, hostedLink, startOnboarding } = useOnboardingStore();
  const [submitIsLoading, setSubmitIsLoading] = React.useState(false);
  const [formState, setFormState] = React.useState<OnboardingForm>({
    country: "",
    organizationType: "",
    legalEntityName: "",
    phoneNumber: "",
    termsChecked: false
  });
  const handleFormChange = (e: React.ChangeEvent<any>) => {
    if (e.target.name === "phoneNumber") {
      const phoneNumber = new AsYouType().input(e.target.value);
      setFormState({
        ...formState,
        phoneNumber
      });
      return;
    }
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };
  useEffect(() => {
    if (hostedLink && hostedLink.length > 0) {
      window.location.href = hostedLink;
    }
  }, [hostedLink]);
  const [errors, setErrors] = React.useState<Partial<Record<keyof OnboardingForm, string>>>({});
  const validateForm = useCallback(() => {
    const newErrors: Partial<Record<keyof OnboardingForm, string>> = {};
    if (!formState.country) {
      newErrors.country = "Please select a country";
    }
    if (!formState.organizationType) {
      newErrors.organizationType = "Please select an organization type";
    }
    if (!formState.legalEntityName) {
      newErrors.legalEntityName = "Please enter a legal entity name";
    }
    if (!formState.phoneNumber) {
      newErrors.phoneNumber = "Please enter a phone number";
    } else if (!isValidPhoneNumber(formState.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    } else if (parsePhoneNumberFromString(formState.phoneNumber)?.country !== formState.country) {
      newErrors.phoneNumber = "Please enter a valid phone number for the selected country";
    }
    if (formState.organizationType === "soleProprietorship") {
      if (!formState.firstName) {
        newErrors.firstName = "Please enter your first name";
      }
      if (!formState.lastName) {
        newErrors.lastName = "Please enter your last name";
      }
    }
    if (!formState.termsChecked) {
      newErrors.termsChecked = "You must accept the terms and conditions to continue";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    formState.country,
    formState.firstName,
    formState.lastName,
    formState.legalEntityName,
    formState.organizationType,
    formState.phoneNumber,
    formState.termsChecked
  ]);
  const { getAccessTokenSilently } = useAuth0();
  const onSubmit = useCallback(async () => {
    if (validateForm()) {
      setSubmitIsLoading(true);
      const { submitOnboardingForm } = useOnboardingStore.getState();
      await submitOnboardingForm(getAccessTokenSilently, {
        country: formState.country as PaymentsPartnerCountry,
        legal_entity_name: formState.legalEntityName,
        legal_entity_type: formState.organizationType as AdyenLegalEntityType,
        first_name: formState.firstName,
        last_name: formState.lastName,
        phone_number:
          parsePhoneNumberFromString(formState.phoneNumber)?.number || formState.phoneNumber
      });
      setSubmitIsLoading(false);
    }
  }, [
    getAccessTokenSilently,
    formState.country,
    formState.firstName,
    formState.lastName,
    formState.legalEntityName,
    formState.organizationType,
    formState.phoneNumber,
    validateForm
  ]);
  const refreshThings = useCallback(async () => {
    await startOnboarding(getAccessTokenSilently);
  }, [startOnboarding, getAccessTokenSilently]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings]);
  const hostedLinkExists = !!(hostedLink && hostedLink.length > 0);
  return (
    <EmptyHeaderLayout>
      {isLoading ||
        (hostedLinkExists && (
          <div className="flex h-full items-center justify-center">
            <LoadingPage />
          </div>
        ))}
      {!isLoading && !hostedLinkExists && (
        <div className="flex h-[calc(100vh-9rem)] w-full overflow-y-auto justify-center">
          <div className="flex flex-col max-w-[32rem]">
            <button
              className="w-fit flex items-center text-black bg-white hover:text-blue-600 mb-4"
              onClick={() => navigate(-1)}
            >
              <ArrowRightIcon className="mr-2 rotate-180" />
              Back
            </button>
            <div className="font-semibold text-2xl">Let's get started with verification</div>
            <div className="text-gray-700">
              To comply with regulations, we are required to collect additional information about
              your business. To start this onboarding process, please complete the form below.
            </div>
            <div className="my-8 flex flex-col text-gray-700">
              <label>Country</label>
              <select
                className="px-2 py-1 border border-gray-300 rounded my-1"
                value={formState.country}
                name="country"
                onChange={handleFormChange}
              >
                <option value="" disabled>
                  Select your country
                </option>
                {onboardingStartView.countries.map(country => (
                  <option key={country.key} value={country.key}>
                    {country.value}
                  </option>
                ))}
              </select>
              {errors.country && <div className="text-red-600">{errors.country}</div>}
              <label className="mt-4">Organization Type</label>
              <select
                className="px-2 py-1 border border-gray-300 rounded my-1"
                value={formState.organizationType}
                name="organizationType"
                onChange={handleFormChange}
              >
                <option value="" disabled>
                  Select your organization type
                </option>
                {onboardingStartView.legal_entities.map(legalEntity => (
                  <option key={legalEntity.key} value={legalEntity.key}>
                    {legalEntity.value}
                  </option>
                ))}
              </select>
              {errors.organizationType && (
                <div className="text-red-600">{errors.organizationType}</div>
              )}
              <label className="mt-4">
                Legal Entity Name
                <sup className="text-red-600">*</sup>
              </label>
              <input
                className="px-2 py-1 border border-gray-300 rounded my-1"
                placeholder="Enter legal entity name"
                value={formState.legalEntityName}
                name="legalEntityName"
                onChange={handleFormChange}
              />
              {errors.legalEntityName && (
                <div className="text-red-600">{errors.legalEntityName}</div>
              )}
              {formState.organizationType === "soleProprietorship" && (
                <>
                  <label className="mt-4">
                    Your first name
                    <sup className="text-red-600">*</sup>
                  </label>
                  <input
                    className="px-2 py-1 border border-gray-300 rounded my-1"
                    placeholder="Enter your first name"
                    value={formState.firstName}
                    name="firstName"
                    onChange={handleFormChange}
                  />
                  {errors.firstName && <div className="text-red-600">{errors.firstName}</div>}
                  <label className="mt-4">
                    Your last name
                    <sup className="text-red-600">*</sup>
                  </label>
                  <input
                    className="px-2 py-1 border border-gray-300 rounded my-1"
                    placeholder="Enter your last name"
                    value={formState.lastName}
                    name="lastName"
                    onChange={handleFormChange}
                  />
                  {errors.lastName && <div className="text-red-600">{errors.lastName}</div>}
                </>
              )}
              <label className="mt-4">
                Phone Number
                <sup className="text-red-600">*</sup>
              </label>
              <input
                type="tel"
                className="px-2 py-1 border border-gray-300 rounded my-1"
                placeholder="Enter phone number"
                value={formState.phoneNumber}
                name="phoneNumber"
                onChange={handleFormChange}
              />
              {errors.phoneNumber && <div className="text-red-600">{errors.phoneNumber}</div>}
            </div>
            <div className="text-gray-700">
              <div>
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={formState.termsChecked}
                  onChange={event =>
                    setFormState({
                      ...formState,
                      termsChecked: event.target.checked
                    })
                  }
                />
                <span
                  className="cursor-pointer"
                  onClick={() =>
                    setFormState({
                      ...formState,
                      termsChecked: !formState.termsChecked
                    })
                  }
                >
                  You confirm to have taken notice of and to accept the following terms and
                  restrictions
                </span>
              </div>
              <ul className="px-4 mt-2 list-disc">
                <li>
                  <a
                    href="https://www.adyen.com/legal/terms-and-conditions-marketpay"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-600"
                  >
                    AfP Terms and Conditions
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.adyen.com/legal/list-restricted-prohibited"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-600"
                  >
                    Adyen Prohibited and Restricted Products and Services list
                  </a>
                </li>
                <li>
                  You agree that a direct debit mandate between you, the Sub-Merchant, and Adyen may
                  be included in the onboarding flow.
                </li>
              </ul>
              {errors.termsChecked && (
                <div className="text-red-600 mt-2">{errors.termsChecked}</div>
              )}
              <div className="text-gray-700 mt-4">
                We will conduct a due diligence before your account will be activated.
              </div>
            </div>
            <button
              className={`px-4 py-1 text-white bg-blue-600 hover:bg-blue-500 w-fit mt-4 rounded flex items-center ${
                submitIsLoading ? "cursor-progress" : ""
              }`}
              onClick={onSubmit}
              disabled={submitIsLoading}
            >
              Continue
              <ArrowRightIcon className="ml-2" />
            </button>
          </div>
        </div>
      )}
    </EmptyHeaderLayout>
  );
};

export default withAuthenticationRequired(OnboardingStartForm, {
  onRedirecting: () => (
    <EmptyHeaderLayout>
      <div className="flex h-full items-center justify-center">
        <LoadingPage />
      </div>
    </EmptyHeaderLayout>
  )
});
